
import { Box, Divider, Stack, Typography } from "@mui/material";
import SectionTabs from "../SectionTabs";
import CompositionHeader from "../CompositionHeader";
import { PatientCard, PractitionerCard, OrganizationSection, HospitalBanner } from "../InfoCards";
import EncounterSection from "../EncounterSection";
import DocumentReferenceSection from "../DocumentReferenceSection";
import { labelSx, valueSx, pillSx, sectionTitleSx, formatDate } from "../styles";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DiagnosticStudiesView = ({ bundle, setShowPdfPreview }: { bundle: any; setShowPdfPreview: (v: boolean) => void }) => {
    const composition = bundle?.composition || {};
    const patient = bundle || {};
    const observations = bundle?.observations || [];
    const conditions = bundle?.conditions || [];
    const procedures = bundle?.procedures || [];

    // Group observations by observationType for better display
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const groupedObservations = observations.reduce((groups: any, obs: any) => {
        const category = obs.observationType || "Test Results";
        if (!groups[category]) groups[category] = [];
        groups[category].push(obs);
        return groups;
    }, {});

    const sections = [
        {
            id: "observations",
            label: "Test Results",
            content:
                observations.length === 0 ? (
                    <Typography sx={labelSx}>No test results recorded.</Typography>
                ) : (
                    <Stack spacing={2}>
                        {Object.entries(groupedObservations).map(([category, obsGroup], catIdx) => (
                            <Box key={`cat-${catIdx}`}>
                                {Object.keys(groupedObservations).length > 1 && (
                                    <Typography sx={{ ...sectionTitleSx, fontSize: "0.9rem", mb: 1 }}>{category}</Typography>
                                )}
                                <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 2, overflow: "hidden" }}>
                                    {/* Table header */}
                                    <Box sx={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", bgcolor: "#f8fafb", borderBottom: "1px solid #e2e8f0", p: 1.5 }}>
                                        <Typography sx={{ ...labelSx, fontWeight: 700, mb: 0 }}>Test</Typography>
                                        <Typography sx={{ ...labelSx, fontWeight: 700, mb: 0 }}>Value</Typography>
                                        <Typography sx={{ ...labelSx, fontWeight: 700, mb: 0 }}>Unit</Typography>
                                        <Typography sx={{ ...labelSx, fontWeight: 700, mb: 0 }}>Status</Typography>
                                    </Box>
                                    {/* Table rows */}
                                    {(obsGroup as any[]).map((obs: any, idx: number) => (
                                        <Box
                                            key={`obs-${idx}`}
                                            sx={{
                                                display: "grid",
                                                gridTemplateColumns: "2fr 1fr 1fr 1fr",
                                                p: 1.5,
                                                borderBottom: idx < (obsGroup as any[]).length - 1 ? "1px solid #f0f0f0" : "none",
                                                transition: "background 0.2s",
                                                "&:hover": { bgcolor: "#f8fffe" }
                                            }}
                                        >
                                            <Typography sx={{ ...valueSx, fontWeight: 600, color: "#1f3a2e" }}>
                                                {obs.observationDisplay || obs.observationType || `Test ${idx + 1}`}
                                            </Typography>
                                            <Typography sx={{ ...valueSx, fontWeight: 700, color: "#2fbf71" }}>
                                                {obs.value || "—"}
                                            </Typography>
                                            <Typography sx={valueSx}>
                                                {obs.unitValue || "—"}
                                            </Typography>
                                            <Typography sx={{ ...pillSx, bgcolor: obs.status === "final" ? "#e8f5e9" : "#e3f2fd", color: obs.status === "final" ? "#2e7d32" : "#1565c0", display: "inline-block", textAlign: "center" }}>
                                                {obs.status || "—"}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Box>
                                {/* Notes for any observation */}
                                {(obsGroup as any[]).some((obs: any) => obs.noteTexts?.length > 0) && (
                                    <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#fffde7", borderRadius: 1, border: "1px solid #fff9c4" }}>
                                        <Typography sx={{ ...labelSx, fontWeight: 600 }}>Notes</Typography>
                                        {(obsGroup as any[]).filter((obs: any) => obs.noteTexts?.length > 0).map((obs: any, oi: number) =>
                                            obs.noteTexts.map((note: string, ni: number) => (
                                                <Typography key={`${oi}-${ni}`} sx={valueSx}>• {note}</Typography>
                                            ))
                                        )}
                                    </Box>
                                )}
                            </Box>
                        ))}
                    </Stack>
                )
        },
        {
            id: "attachments",
            label: "Attachments",
            content: bundle?.documentReference || bundle?.binary?.data ? (
                <DocumentReferenceSection
                    documentReference={bundle?.documentReference}
                    binary={bundle?.binary}
                    onPreviewBase64={() => setShowPdfPreview(true)}
                />
            ) : (
                <Typography sx={labelSx}>No attachments available.</Typography>
            )
        },
        ...(conditions.length > 0 ? [{
            id: "conditions",
            label: "Conditions",
            content: (
                <Stack spacing={1}>
                    {conditions.map((cond: any, idx: number) => (
                        <Box key={idx} sx={{ p: 1.5, border: "1px solid #f0f0f0", borderRadius: 1.5, "&:hover": { borderColor: "#2fbf71" } }}>
                            <Typography sx={{ ...valueSx, fontWeight: 600 }}>{cond.display || cond.code || "Condition"}</Typography>
                            {cond.clinicalStatus && <Typography sx={labelSx}>Status: {cond.clinicalStatus}</Typography>}
                        </Box>
                    ))}
                </Stack>
            )
        }] : []),
        ...(procedures.length > 0 ? [{
            id: "procedures",
            label: "Procedures",
            content: (
                <Stack spacing={1}>
                    {procedures.map((proc: any, idx: number) => (
                        <Box key={idx} sx={{ p: 1.5, border: "1px solid #f0f0f0", borderRadius: 1.5, "&:hover": { borderColor: "#2fbf71" } }}>
                            <Typography sx={{ ...valueSx, fontWeight: 600 }}>{proc.display || proc.code || "Procedure"}</Typography>
                            {proc.status && <Typography sx={labelSx}>Status: {proc.status}</Typography>}
                            {proc.performedDateTime && <Typography sx={labelSx}>Date: {formatDate(proc.performedDateTime)}</Typography>}
                        </Box>
                    ))}
                </Stack>
            )
        }] : [])
    ];

    return (
        <Stack spacing={3}>
            <HospitalBanner organization={bundle?.organization} />
            <CompositionHeader composition={composition} />

            <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mt: "8px !important" }}>
                <PatientCard patient={patient} />
                <PractitionerCard practitioner={patient} />
            </Stack>

            <OrganizationSection organization={bundle?.organization} />
            <EncounterSection encounter={bundle?.encounter} />

            <Divider />
            <SectionTabs sections={sections} initialId="observations" />
        </Stack>
    );
};

export default DiagnosticStudiesView;
